<script setup lang="ts">
import type { Field, TableSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';

import { computed } from 'vue';

import FieldRenderer from './FieldRenderer.vue';

defineProps<{
  section: TableSection;
}>();

const reportStore = useUpdatedReportStore();

const isEditing = computed(() => reportStore.isEditing);

function isCellEditable(cell: Field): boolean {
  const storeState = reportStore.values?.[cell.key];
  const isEditable
    = storeState?.isEditable !== undefined
      ? storeState.isEditable
      : cell.isEditable ?? true;
  const isDisabled
    = storeState?.isDisabled !== undefined
      ? storeState.isDisabled
      : cell.isDisabled ?? false;
  return isEditable && !isDisabled;
}

function hasEditableCells(row: { cells: Field[] }): boolean {
  return row.cells.some(isCellEditable);
}

function clearRow(row: { cells: Field[] }) {
  row.cells.forEach((cell) => {
    if (isCellEditable(cell)) {
      reportStore.setValue(cell.key, null);
    }
  });
}

const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const titleStyle = computed(() => {
  if (!theme.value) return {};
  const sl = theme.value.fonts.sectionLabel;
  return {
    fontFamily: sl.family,
    fontSize: `${sl.size}px`,
    fontWeight: sl.weight,
    color: theme.value.colors.text,
    textTransform: sl.uppercase ? 'uppercase' : 'none',
    letterSpacing: sl.letterSpacing ? `${sl.letterSpacing}px` : undefined,
  };
});

const headerStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderBottom: `1px solid ${theme.value.colors.border}`,
    paddingBottom: '4px',
    marginBottom: `${theme.value.spacing.fieldGap / 2}px`,
  };
});

const tableStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderColor: theme.value.colors.border,
    borderRadius: `${theme.value.borderRadius}px`,
  };
});

const thStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    fontWeight: 600,
    color: theme.value.colors.text,
    background: theme.value.colors.tableHeaderBg,
    borderColor: theme.value.colors.border,
  };
});

const labelCellStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    fontWeight: theme.value.fonts.label.weight,
    color: theme.value.colors.text,
    borderColor: theme.value.colors.border,
  };
});

const cellStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderColor: theme.value.colors.border,
  };
});

const footnoteStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.body.family,
    fontSize: `${theme.value.fonts.body.size - 1}px`,
    color: theme.value.colors.muted,
    fontStyle: 'italic',
  };
});
</script>

<template>
  <div class="table-section">
    <!-- Title -->
    <div v-if="section.title" class="table-header-row" :style="headerStyle">
      <span class="table-title" :style="titleStyle">
        {{ section.title }}
      </span>
    </div>

    <!-- Table -->
    <table class="report-table" :style="tableStyle">
      <thead>
        <tr>
          <th
            v-for="(col, ci) in section.columns"
            :key="ci"
            class="table-th"
            :style="thStyle"
          >
            {{ col }}
          </th>
          <th v-if="isEditing" class="table-th action-th" :style="thStyle" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in section.rows" :key="row.id">
          <!-- Row label -->
          <td class="table-td table-row-label" :style="labelCellStyle">
            {{ row.label }}
          </td>
          <!-- Data cells -->
          <td
            v-for="cell in row.cells"
            :key="cell.key"
            class="table-td"
            :style="cellStyle"
          >
            <FieldRenderer :field="cell" />
          </td>
          <!-- Actions column -->
          <td v-if="isEditing" class="table-td action-td" :style="cellStyle">
            <button
              v-if="hasEditableCells(row)"
              class="clear-row-btn"
              title="Clear row values"
              @click="clearRow(row)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="clear-icon"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Footnote -->
    <p v-if="section.footnote" class="table-footnote" :style="footnoteStyle">
      {{ section.footnote }}
    </p>
  </div>
</template>

<style scoped>
.table-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.table-header-row {
  margin-bottom: 2px;
}

.table-title {
  font-size: 11px;
  font-weight: 600;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
}

.table-th {
  text-align: left;
  padding: 4px 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 9px;
  font-weight: 600;
}

.table-td {
  padding: 3px 6px;
  border: 1px solid #e2e8f0;
  vertical-align: middle;
}

.table-row-label {
  font-size: 9px;
  font-weight: 500;
  white-space: nowrap;
}

.table-footnote {
  font-size: 8px;
  color: #888;
  font-style: italic;
  margin: 2px 0 0 0;
}

.action-th,
.action-td {
  padding: 0;
  text-align: center;
}

.clear-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--md-tertiary);
  cursor: pointer;
  opacity: 1;
  padding: 0;
}

.clear-icon {
  width: 12px;
  height: 12px;
}
</style>
